import type { InputType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import {
  AutoComplete,
  ButtonGroup,
  Checkbox,
  Input2,
  InputEmail,
  InputFile,
  InputFileTable,
  InputRating,
  InputVisibleLogic,
  InputWithButton,
  MultiSelect,
  OptionSelect,
  RadioGroup,
  SearchableSelect,
  Select2,
  SelectOptionInputs,
  TextArea,
  Toggle,
  ToggleButton,
} from 'stories/components'
import { ButtonGroupSelector } from 'stories/components/ButtonGroupSelector/ButtonGroupSelector'
import { InputFormTable } from 'stories/components/FormTable/InputFormTable'
import { GoogleAutoComplete } from 'stories/components/GoogleAutoComplete/GoogleAutoComplete'
import { GoogleMap } from 'stories/components/GoogleMap'

interface Prop {
  input: InputType
  Key: string
  onChange: Function
  showHistory?: Function
  onBlur?: Function | null
  onFocus?: Function | null
  onKeyUp?: Function | null
  onClick?: Function | null
}

export const RenderInput = (props: Prop) => {
  const {
    input,
    Key,
    onChange,
    showHistory = () => {},
    onBlur = null,
    onFocus = null,
    onKeyUp = null,
    onClick = null,
  } = props
  let {
    fontSize,
    history,
    prefix,
    title,
    rows,
    error = '',
    disabled,
    value = '',
    icon = '',
    readOnly = false,
    sort = false,
    copyable = false,
    defaultSelected = undefined,
    multiple = false,
    needCountyUpdate = true,
    tooltip = '',
    acceptFileTypes,
    required,
    additionalElements,
    bgColor,
    placeholder,
    autoComplete = false,
  } = input
  required = required && !disabled
  if (!(input.inputType as any)) input.inputType = 'Text' as any
  if (disabled && ['Map', 'map'].indexOf(input.inputType) !== -1) input.inputType = 'Text'

  const { hasPermission } = usePermissions()
  if (!hasPermission('CAN_VIEW_AUDIT_LOG')) history = false

  switch (input.inputType) {
    case 'Text':
    case 'text':
      return (
        <Input2
          autoComplete={autoComplete}
          title={title}
          bgColor={bgColor}
          prefix={prefix}
          hasIcon={icon !== ''}
          icon={icon}
          key={Key}
          name={Key}
          type={input.type}
          error={error}
          value={value as string}
          fontSize={fontSize}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          required={required}
          history={history}
          copyable={copyable}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          onBlurNull={onBlur === null}
          showHistory={() => showHistory(Key)}
          placeholder={placeholder}
          indent={input.indent}
          options={input.options}
          showLinkDir={input.showLinkDir}
          userSearch={input.userSearch}
          onFocus={(event) => onFocus && onFocus(event, Key)}
          onKeyUp={(event) => onKeyUp && onKeyUp(event)}
          onClick={(event) => onClick && onClick(event)}
          decimalStep={input.decimalStep}
        />
      )
    case 'Select':
    case 'select':
      return (
        <Select2
          id={Key}
          bgColor={bgColor}
          name={Key}
          title={title}
          options={input.options}
          key={Key}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          sort={sort}
          tooltip={tooltip}
          value={input.value as string}
          hasDefaultOption={input.hasDefaultOption}
          defaultOptionText={input.defaultOptionText}
          required={required}
          history={history}
          copyable={copyable}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          additionalElements={additionalElements}
        />
      )
    case 'OptionSelect':
      return (
        <OptionSelect
          key={Key}
          title={title}
          options={input.options}
          disabled={disabled}
          onChange={(value) => onChange(Key, value)}
          sort={sort}
          value={value}
        />
      )
    case 'MultiSelect':
    case 'multiselect':
      return (
        <MultiSelect
          id={Key}
          title={title}
          options={input.options}
          key={Key}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          value={input.value as Record<string, boolean>}
          required={required}
          additionalElements={additionalElements}
          history={history}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          defaultSelected={defaultSelected}
          sort={input.sort}
          sortKey={input.sortKey}
          prefix={input.prefix}
          className={input.className}
        />
      )
    case 'SearchableSelect':
    case 'searchableselect':
      return (
        <SearchableSelect
          id={Key}
          bgColor={bgColor}
          name={Key}
          title={title}
          options={input.options}
          key={Key}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          sort={sort}
          tooltip={tooltip}
          value={input.value as string}
          hasDefaultOption={input.hasDefaultOption}
          defaultOptionText={input.defaultOptionText}
          required={required}
          history={history}
          copyable={copyable}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          additionalElements={additionalElements}
        />
      )
    case 'Check':
    case 'check':
      return (
        <ToggleButton
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          tooltip={tooltip}
          value={input.value as boolean}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          label={['Yes', 'No']}
          textLeft={input.textLeft}
        />
      )
    case 'CheckBox':
    case 'checkbox':
      return (
        <Checkbox
          className={`pt-1 ${input.className}`}
          id={Key}
          title={title}
          color={input.color}
          key={Key}
          disabled={disabled}
          value={input.value as boolean}
          history={history}
          error={error}
          tooltip={tooltip}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          size={input.size}
          fontClass={input.fontSize ? `text-[${input.fontSize}px]` : ''}
        />
      )
    case 'Radio':
    case 'radio':
      return (
        <RadioGroup
          id={Key}
          title={title}
          options={input.options}
          key={Key}
          name={Key}
          error={error}
          disabled={disabled}
          value={input.value as string}
          required={required}
          history={history}
          size={input.size}
          titleType={input.titleType}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          noBorder={input.noBorder}
          isPlain={input.isPlain}
        />
      )
    case 'TextArea':
    case 'textarea':
      return (
        <TextArea
          className="pt-1"
          title={title}
          key={Key}
          rows={rows}
          error={error}
          disabled={disabled}
          value={input.value as string}
          history={history}
          required={required}
          additionalElements={additionalElements}
          onBlur={() => onBlur && onBlur(Key)}
          onBlurNull={onBlur === null}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          onFocus={(event) => onFocus && onFocus(event, Key)}
          onKeyUp={(event) => onKeyUp && onKeyUp(event)}
          onClick={(event) => onClick && onClick(event)}
        />
      )
    case 'Map':
    case 'map':
      return (
        <GoogleAutoComplete
          title={title}
          key={Key}
          inputKey={Key}
          error={error}
          value={value as string}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          needCountyUpdate={needCountyUpdate}
          tooltip={tooltip}
          requireValidate={input.requireValidate}
          allowForeign={input.allowForeign}
          sitexPro={input.sitexPro}
          onChange={(value) => onChange(Key, value)}
          additionalElements={additionalElements}
          onBlur={() => onBlur && onBlur(Key)}
          history={history}
          showHistory={() => showHistory(Key)}
        />
      )
    case 'GoogleMap':
    case 'googleMap':
      return <GoogleMap key={Key} markers={[{ title: title || '', lat: value.lat, lng: value.lng }]} />
    case 'Toggle':
    case 'toggle':
      return (
        <Toggle
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          value={input.value as boolean}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
        />
      )
    case 'ToggleButton':
    case 'togglebutton':
      return (
        <ToggleButton
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          value={input.value as boolean}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          textLeft={input.textLeft}
        />
      )
    case 'File':
    case 'file':
      return (
        <InputFile
          className="pt-1"
          title={title}
          key={Key}
          acceptFileTypes={acceptFileTypes}
          disabled={disabled}
          value={input.value}
          error={error}
          multiple={multiple}
          history={history}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          downloadable={input.downloadable}
          acceptDocument={input.acceptDocument}
        />
      )
    case 'FileTable':
    case 'filetable':
      return (
        <InputFileTable
          className="pt-1"
          title={title}
          filePath={input.filePath}
          key={Key}
          name={Key}
          acceptFileTypes={acceptFileTypes}
          disabled={disabled}
          value={input.value}
          error={error}
          history={history}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          showCategory={input.showCategory}
          showStatus={input.showStatus}
          disableNameChange={input.disableNameChange}
          multiple={input.multiple}
          categories={input.categories}
          customFields={input.customFields}
          additionalActions={input.additionalActions}
          additionalRowActions={input.additionalRowActions}
          canDelete={input.canDelete}
          canEditCategory={input.canEditCategory}
        />
      )
    case 'AutoComplete':
    case 'autocomplete':
      return (
        <AutoComplete
          className="pt-1"
          title={title}
          key={Key}
          value={value}
          disabled={disabled}
          error={error}
          type={input.type}
          tooltip={tooltip}
          additionalElements={additionalElements}
          userSearch={input.userSearch}
          autoCompleteType={input.autoCompleteType}
          required={input.required}
          onBlur={() => onBlur && onBlur(Key)}
          history={history}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          checkExisting={input.checkExisting}
        />
      )
    case 'InputWithButton':
    case 'inputwithbutton':
      return (
        <InputWithButton
          className=""
          title={title}
          key={Key}
          value={value}
          disabled={disabled}
          multiple={input.multiple}
          error={error}
          type={input.type}
          required={input.required}
          options={input.options}
          modalTitle={input.modalTitle}
          enableInput={input.enableInput}
          onChange={(value: any) => onChange(Key, value)}
        />
      )
    case 'email':
      return (
        <InputEmail
          className=""
          title={title}
          key={Key}
          value={value}
          disabled={disabled}
          error={error}
          required={input.required}
          defaultEmails={input.defaultEmails}
          modalTitle={input.modalTitle}
          onChange={(value: any) => onChange(Key, value)}
        />
      )
    case 'Table':
    case 'table':
      return (
        <InputFormTable
          inputs={input.inputs}
          data={input.value}
          onChange={(value: any) => onChange(Key, value)}
          error={error}
          permission={disabled ? 3 : 1}
        />
      )

    case 'Section':
    case 'section':
      return (
        <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center`}>
          <p>- {title}</p>
          {additionalElements}
        </div>
      )

    case 'Group':
    case 'group':
      return (
        <div className={`w-full bg-gray-200 p-2`}>
          <p>{title}</p>
        </div>
      )
    case 'Label':
    case 'label':
      return (
        <div className={`w-full italic p-2`}>
          <p dangerouslySetInnerHTML={{ __html: title }}></p>
        </div>
      )

    case 'Title':
    case 'title':
      return (
        <div className={`w-full p-2 text-sm`}>
          <p dangerouslySetInnerHTML={{ __html: title }}></p>
        </div>
      )

    case 'Value':
    case 'value':
      return (
        <div className={`w-full p-2`}>
          <p dangerouslySetInnerHTML={{ __html: value }}></p>
        </div>
      )

    case 'Custom':
    case 'custom':
      return input.render ? input.render(input.value, (value: any) => onChange(Key, value), input) : null
    case 'alert':
      return (
        <div className={`input md:col-span-${input.span} mb-4`}>
          <div
            className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded relative text-[15px]"
            role="alert"
          >
            <span dangerouslySetInnerHTML={{ __html: input.value }} />
          </div>
        </div>
      )

    case 'Rating':
    case 'rating':
      return (
        <InputRating
          title={input.title}
          max={input.max}
          value={input.value}
          required={input.required}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(value: any) => onChange(Key, value)}
        />
      )

    case 'ButtonGroup':
    case 'buttongroup':
      return (
        <ButtonGroup
          title={input.options}
          className={input.className}
          value={input.value}
          onChange={(value: any) => onChange(Key, value)}
        />
      )

    case 'ButtonGroupSelector':
    case 'buttongroupselector':
      return (
        <ButtonGroupSelector
          title={input.title}
          options={input.options}
          className={input.className}
          value={input.value}
          disabled={input.disabled}
          required={input.required}
          error={input.error}
          onChange={(value: any) => onChange(Key, value)}
        />
      )
    case 'VisibleLogic':
    case 'visibleLogic':
      return (
        <InputVisibleLogic
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          value={input.value}
          fieldTitles={input.fieldTitles}
          fieldInputs={input.fieldInputs}
          history={history}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
        />
      )

    case 'SelectOptionInput':
      return (
        <SelectOptionInputs title={input.title} value={input.value} onChange={(value: any) => onChange(Key, value)} />
      )
  }
  return null
}
